import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  MAX_AUTO_ATTEMPTS,
  getChatConn,
  getChatConnStatus,
  registerChatReconnect,
  requestChatReconnect,
  resetChatConn,
  setChatConnStatus,
  type ChatReconnectResult,
} from '@/lib/chatConnection';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { mencaoPara } from '@/lib/mencoesChat';
import { montarNotificacao } from '@/lib/chatNotifications';

/**
 * Hook global de notificações do ChatContar.
 *
 * - Pede permissão de notificação do navegador uma vez por sessão.
 * - Assina em tempo real os INSERTs em chat_messages das conversas do usuário.
 * - Quando chega mensagem de outro usuário:
 *    - toca um som curto (Web Audio API, sem asset),
 *    - exibe notificação nativa quando a aba está em segundo plano,
 *    - pisca o document.title até a aba voltar a ficar visível.
 * - Clicar na notificação foca a janela e navega para /chat.
 * - Mantém o estado de conexão em `@/lib/chatConnection`, que alimenta o
 *   alerta global de desconexão.
 */

/** Sessão que ainda dá para usar no realtime (com folga de 30s). */
function sessaoAindaVale(s: Session | null): s is Session {
  if (!s?.access_token) return false;
  if (!s.expires_at) return true;
  return s.expires_at * 1000 - Date.now() > 30_000;
}

/**
 * Devolve uma sessão utilizável, renovando o token quando preciso.
 *
 * É a peça que faltava: o realtime guarda o JWT de quando o socket subiu, e
 * reabrir o canal com o token vencido falha exatamente igual — que é por que o
 * botão "Reconectar" parecia não fazer nada e só entrar de novo resolvia.
 */
async function obterSessaoValida(): Promise<Session | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const atual = data.session ?? null;
    if (sessaoAindaVale(atual)) return atual;
    const { data: renovada } = await supabase.auth.refreshSession();
    const nova = renovada.session ?? null;
    return sessaoAindaVale(nova) ? nova : null;
  } catch {
    return null;
  }
}

/** Erro de consulta que na verdade é sessão inválida, não falha de rede. */
function erroDeSessao(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false;
  if (err.code === 'PGRST301' || err.code === '401') return true;
  return /jwt|token|expired|unauthor/i.test(err.message || '');
}

export function useChatNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Mesma contagem real usada no sininho do chat — evita que o número que
  // pisca no título seja só uma contagem de eventos de INSERT desde a última
  // vez que a aba ficou visível (o que dessincroniza rápido do que está
  // realmente marcado como não lido no banco).
  const unreadTotal = useUnreadMessages();
  const unreadTotalRef = useRef(unreadTotal);
  useEffect(() => { unreadTotalRef.current = unreadTotal; }, [unreadTotal]);

  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const originalTitleRef = useRef<string>(typeof document !== 'undefined' ? document.title : '');
  const flashIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const profilesCacheRef = useRef<Map<string, string>>(new Map());
  const groupNamesCacheRef = useRef<Map<string, string | null>>(new Map());

  // Pede permissão (sem ser intrusivo: apenas se "default")
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      // Aguarda primeira interação do usuário para evitar bloqueio
      const ask = () => {
        Notification.requestPermission().catch(() => {});
        window.removeEventListener('click', ask);
        window.removeEventListener('keydown', ask);
      };
      window.addEventListener('click', ask, { once: true });
      window.addEventListener('keydown', ask, { once: true });
      return () => {
        window.removeEventListener('click', ask);
        window.removeEventListener('keydown', ask);
      };
    }
  }, []);

  // Para o flash quando a aba volta a ficar visível
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        stopTitleFlash();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  function stopTitleFlash() {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      flashIntervalRef.current = null;
    }
    if (originalTitleRef.current) document.title = originalTitleRef.current;
  }

  function startTitleFlash() {
    if (flashIntervalRef.current) return;
    if (!originalTitleRef.current) originalTitleRef.current = document.title;
    let toggle = false;
    flashIntervalRef.current = setInterval(() => {
      toggle = !toggle;
      const n = unreadTotalRef.current;
      document.title = toggle
        ? `(${n}) Nova mensagem • ChatContar`
        : originalTitleRef.current;
    }, 1200);
  }

  // Some mensagens em outro dispositivo (ou noutra aba) zera o total real —
  // encerra o flash mesmo sem a aba ter voltado a ficar visível aqui.
  useEffect(() => {
    if (unreadTotal === 0) stopTitleFlash();
  }, [unreadTotal]);

  function playBeep() {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current!;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(880, now);
      o.frequency.exponentialRampToValueAtTime(660, now + 0.18);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(now);
      o.stop(now + 0.4);
    } catch {
      // ignore
    }
  }

  async function getSenderName(senderId: string): Promise<string> {
    const cached = profilesCacheRef.current.get(senderId);
    if (cached) return cached;
    const { data } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('user_id', senderId)
      .maybeSingle();
    const name = (data?.full_name || 'Nova mensagem').trim();
    profilesCacheRef.current.set(senderId, name);
    return name;
  }

  /** Nome do grupo, ou null em conversa direta — é o que vira o título da notificação. */
  async function getGroupName(conversationId: string): Promise<string | null> {
    if (groupNamesCacheRef.current.has(conversationId)) return groupNamesCacheRef.current.get(conversationId) ?? null;
    const { data } = await supabase
      .from('chat_conversations')
      .select('nome, is_group')
      .eq('id', conversationId)
      .maybeSingle();
    const nome = data?.is_group ? (data.nome || 'Grupo') : null;
    groupNamesCacheRef.current.set(conversationId, nome);
    return nome;
  }

  useEffect(() => {
    if (!user) return;

    let participantIds: Set<string> = new Set();
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    let autoTimer: ReturnType<typeof setTimeout> | null = null;

    const derrubarCanal = () => {
      const antigo = channel;
      if (!antigo) return;
      // Zera a referência ANTES de remover: `removeChannel` dispara o callback
      // do canal antigo com status CLOSED, e sem essa guarda a própria
      // reconexão se marcava como desconectada logo após o clique no botão.
      channel = null;
      try { supabase.removeChannel(antigo); } catch { /* ignore */ }
    };

    const agendarTentativaAutomatica = () => {
      if (autoTimer || cancelled) return;
      const { attempts } = getChatConn();
      // Passando do limite, para de tentar sozinho: o alerta já está dizendo
      // que é caso de entrar de novo, e o botão continua à disposição.
      if (attempts >= MAX_AUTO_ATTEMPTS) return;
      const espera = Math.min(30_000, 3_000 * 2 ** attempts);
      autoTimer = setTimeout(() => {
        autoTimer = null;
        if (cancelled) return;
        if (getChatConnStatus() !== 'disconnected') return;
        void requestChatReconnect().then((res) => {
          if (res === 'failed') agendarTentativaAutomatica();
        });
      }, espera);
    };

    const marcarQueda = (motivo: string) => {
      const anterior = getChatConnStatus();
      if (anterior === 'reconnecting') return;
      setChatConnStatus('disconnected', motivo);
      if (anterior !== 'disconnected' && anterior !== 'expired') playBeep();
      agendarTentativaAutomatica();
    };

    const conectar = (): Promise<ChatReconnectResult> =>
      new Promise<ChatReconnectResult>((resolve) => {
      void (async () => {
      const sessao = await obterSessaoValida();
      if (cancelled) return resolve('failed');
      if (!sessao) return resolve('expired');

      // Empurra o token novo para o socket do realtime antes de reabrir.
      try { supabase.realtime.setAuth(sessao.access_token); } catch { /* ignore */ }

      const { data: parts, error } = await supabase
        .from('chat_participants')
        .select('conversation_id')
        .eq('user_id', user.id);
      if (cancelled) return resolve('failed');
      if (error) return resolve(erroDeSessao(error) ? 'expired' : 'failed');
      participantIds = new Set((parts || []).map((p: any) => p.conversation_id));

      derrubarCanal();

      let respondido = false;
      const meu = supabase
        .channel(`chat-notify-${user.id}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'chat_messages' },
          async (payload) => {
            const msg: any = payload.new;
            if (!msg) return;
            if (msg.sender_id === user.id) return;
            if (!participantIds.has(msg.conversation_id)) return;

            const onChatPage = locationRef.current === '/chat';
            const hidden = document.visibilityState !== 'visible';

            // Se já está na página de chat com a aba visível, deixa a UI do chat lidar
            if (onChatPage && !hidden) return;

            const [senderName, groupName] = await Promise.all([
              getSenderName(msg.sender_id),
              getGroupName(msg.conversation_id),
            ]);
            const preview =
              (msg.content && String(msg.content).slice(0, 140)) ||
              (msg.file_name ? `📎 ${msg.file_name}` : 'Enviou uma mensagem');
            const { titulo, corpo } = montarNotificacao({
              remetente: senderName,
              grupo: groupName,
              texto: preview,
              mencao: mencaoPara(msg, user.id),
            });

            playBeep();
            startTitleFlash();

            if ('Notification' in window && Notification.permission === 'granted' && hidden) {
              try {
                const n = new Notification(titulo, {
                  body: corpo,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico',
                  tag: `chat-${msg.conversation_id}`,
                  ...({ renotify: true } as any),
                });
                n.onclick = () => {
                  window.focus();
                  navigate('/chat');
                  n.close();
                };
              } catch {
                // ignore
              }
            }
          },
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'chat_participants', filter: `user_id=eq.${user.id}` },
          (payload: any) => {
            if (payload?.new?.conversation_id) participantIds.add(payload.new.conversation_id);
          },
        );

      channel = meu;

      meu.subscribe((status) => {
        // Canal já substituído por outra tentativa: o status é do passado.
        if (cancelled || channel !== meu) return;
        if (status === 'SUBSCRIBED') {
          setChatConnStatus('connected');
          if (!respondido) { respondido = true; resolve('connected'); }
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          if (!respondido) { respondido = true; resolve('failed'); return; }
          marcarQueda('A conexão com o ChatContar caiu.');
        }
      });
      })();
      });

    const tentarSeCaido = () => {
      if (cancelled) return;
      const status = getChatConnStatus();
      if (status === 'connected' || status === 'reconnecting') return;
      void requestChatReconnect().then((res) => {
        if (res === 'failed') agendarTentativaAutomatica();
      });
    };

    setChatConnStatus('connecting');
    registerChatReconnect(conectar);
    void requestChatReconnect().then((res) => {
      if (res === 'failed') agendarTentativaAutomatica();
    });

    // Reconecta sozinho quando a rede volta, quando a aba fica visível e
    // quando o supabase renova o token por conta própria.
    const onOnline = () => tentarSeCaido();
    const onVisible = () => {
      if (document.visibilityState === 'visible') tentarSeCaido();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    const { data: authSub } = supabase.auth.onAuthStateChange((event, sessao) => {
      if (cancelled) return;
      if (event === 'TOKEN_REFRESHED' && sessao?.access_token) {
        try { supabase.realtime.setAuth(sessao.access_token); } catch { /* ignore */ }
        tentarSeCaido();
      }
    });

    return () => {
      cancelled = true;
      if (autoTimer) clearTimeout(autoTimer);
      registerChatReconnect(null);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      try { authSub.subscription.unsubscribe(); } catch { /* ignore */ }
      derrubarCanal();
      stopTitleFlash();
      resetChatConn();
    };
  }, [user, navigate]);
}
